import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminQuestionPostUrl, getTagUrl } from '../utils/UrlUtilities';
import { constructQuestionPostPayload } from '../utils/PayloadUtils';
import {
    BCS_LEVEL,
    QUESTIONDIFFICULTY,
    QUESTION_TYPES,
    QUESTION_TYPE_LABELS,
    TC_BLANK_LABELS,
    SIMILARITY_THRESHOLDS,
    SYLLABUS_STATUS_OPTIONS,
    TAGLEVELS,
    BOARD_VALUES,
    DIVISION_VALUES,
    UNIT_VALUES,
    isBoardLevel,
    isDivisionLevel,
    isUnitLevel,
    isLanguageLevel,
    isTimerLevel,
    isEssaySubject,
    sortBcsTerms,
} from '../utils/Constants';
import useQuestionAi from '../utils/useQuestionAi';
import useVoiceDictation from '../utils/useVoiceDictation';
import { normalizeFieldText, beautifyText } from '../utils/BasicUtilities';
import VoiceInputButton from './VoiceInputButton';
import MediaUploadField from './MediaUploadField';
import MathText from './ui/MathText';
import SimilarQuestionsPanel from './ingestion/SimilarQuestionsPanel';
import SourceDocViewer from './SourceDocViewer';
import CloseButton from './ui/CloseButton';
import QuestionPreviewModal from './QuestionPreviewModal';
import YearPicker from './ui/YearPicker';
import './components.css';


const QuestionAddForm = ({ onCancel, onSave, onSaved = null, onBack = null, backLabel = '◀ Back', groupId = null, groupOrder = null, defaultLevel = '', defaultDivision = '', defaultUnit = '', defaultSubject = '', defaultTopic = '', defaultExam = '', defaultYear = '', passageStartCount = 0, title = 'Add New Question', cancelLabel = 'Cancel', addAnotherLabel = 'Save and Add Another', saveLabel = 'Save', sourceDoc = null, setSourceDoc = null }) => {
    const [questionText, setQuestionText] = useState('');
    const [optionA, setOptionA] = useState('');
    const [optionB, setOptionB] = useState('');
    const [optionC, setOptionC] = useState('');
    const [optionD, setOptionD] = useState('');
    // Optional extra options E–H (revealed two at a time). Simple text inputs (no voice).
    const [optionE, setOptionE] = useState('');
    const [optionF, setOptionF] = useState('');
    const [optionG, setOptionG] = useState('');
    const [optionH, setOptionH] = useState('');
    // How many extra options are revealed: 0, 2 (E,F) or 4 (E–H).
    const [extraOptionsShown, setExtraOptionsShown] = useState(0);
    // GRE Text Completion blanks: [{ choices: ['','',''], correct: 0 }, ...] (2–3 blanks).
    const [blanks, setBlanks] = useState([
        { choices: ['', '', ''], correct: 0 },
        { choices: ['', '', ''], correct: 0 },
    ]);
    const [correctOptions, setCorrectOptions] = useState([]); // multi-answer: e.g. ['A','C']
    // Build-a-Sentence: the correct tile order as 0-based indices into the present option letters.
    const [correctOrder, setCorrectOrder] = useState([]);
    const [questionType, setQuestionType] = useState('MCQ');
    const [textAnswer, setTextAnswer] = useState('');
    const [answerExplanation, setAnswerExplanation] = useState('');
    const [level, setLevel] = useState(defaultLevel || '');
    // Multi-level: a question can target several levels; `level` mirrors the first (primary).
    const [levels, setLevels] = useState(defaultLevel ? [defaultLevel] : []);
    const [selectedSubjects, setSelectedSubjects] = useState(defaultSubject ? [defaultSubject] : []);
    const [selectedTopics, setSelectedTopics] = useState(defaultTopic ? [defaultTopic] : []);
    const [selectedExams, setSelectedExams] = useState(defaultExam ? [defaultExam] : []);
    const [selectedBcsTerm, setSelectedBcsTerm] = useState('');
    const [board, setBoard] = useState('');
    const [division, setDivision] = useState(defaultDivision || '');
    const [unit, setUnit] = useState(defaultUnit || '');
    const [difficulty, setDifficulty] = useState('MEDIUM');
    const [selectedYears, setSelectedYears] = useState(defaultYear ? [defaultYear] : []);
    const [syllabusStatus, setSyllabusStatus] = useState('CURRENT');
    const [sideText, setSideText] = useState('');
    const [audioUrl, setAudioUrl] = useState('');
    const [solvingTimeSeconds, setSolvingTimeSeconds] = useState('');
    const [questionImageUrl, setQuestionImageUrl] = useState('');
    const [optionImages, setOptionImages] = useState({ A: '', B: '', C: '', D: '', E: '', F: '', G: '', H: '' });
    const [explanationImageUrl, setExplanationImageUrl] = useState('');
    // IELTS accepted-answer alternatives for TEXT gap-fills (one per line or comma-separated) —
    // stored in metadata.accepted; the grader also expands answer-key notation (10/ten, grass(es)).
    const [acceptedAnswers, setAcceptedAnswers] = useState('');
    // IELTS MATCHING: the shared option box (letter → text) + which letter(s) are correct for this
    // item, plus an optional shared map/plan/diagram image. Box lives in metadata.match_options.
    const [matchRows, setMatchRows] = useState([{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }]);
    const [matchImage, setMatchImage] = useState('');

    const correctOption = [...correctOptions].sort().join(',');
    const isText = questionType === 'TEXT';
    const isTC = questionType === 'TEXT_COMPLETION';
    const isSE = questionType === 'SENTENCE_EQUIVALENCE';
    const isBuild = questionType === 'BUILD_SENTENCE';
    const isMatching = questionType === 'MATCHING';
    const isEssay = selectedSubjects.some(isEssaySubject);
    // MCQ (non-Essay) / SE need options + a correct set; TEXT / TEXT_COMPLETION / BUILD / MATCHING /
    // Essay relax that.
    const optionsRequired = !isText && !isTC && !isBuild && !isMatching && !isEssay;
    // The option grid shows for MCQ + SE + BUILD (BUILD uses the option fields as its word tiles).
    // TEXT_COMPLETION uses the blanks editor; MATCHING uses its own shared-box editor.
    const showOptionsGrid = !isText && !isTC && !isMatching;
    // Ordered option letters currently visible: A–D always, plus revealed extras; SE forces 6.
    const visibleLetters = isSE
        ? ['A', 'B', 'C', 'D', 'E', 'F']
        : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].slice(0, 4 + extraOptionsShown);
    const optionsByLetter = { A: optionA, B: optionB, C: optionC, D: optionD, E: optionE, F: optionF, G: optionG, H: optionH };
    const optionSetters = { A: setOptionA, B: setOptionB, C: setOptionC, D: setOptionD, E: setOptionE, F: setOptionF, G: setOptionG, H: setOptionH };
    // Letters with content (text or image) = present options; correct set must live within these.
    const presentLetters = visibleLetters.filter(l => (optionsByLetter[l] || '').trim() || optionImages[l]);
    // Exam-driven fields: Board + Exam Year appear (and are required) once an exam is picked.
    const examSelected = selectedExams.length > 0;
    const divisionRequired = isDivisionLevel(level);
    const boardRequired = isBoardLevel(level) && examSelected;
    // A "real" BCS question is one whose EXAM is BCS (level alone isn't enough — a BCS-level
    // question may belong to a non-BCS exam). Only then is a BCS term the right grouping, and
    // the term replaces the exam year (BCS sittings carry no year).
    const isBcs = level === BCS_LEVEL && selectedExams.some(e => e.toUpperCase() === 'BCS');
    const bcsTermRequired = isBcs;
    // Exam Year: visible/required when an exam is picked — but NOT for BCS exams, where the
    // mandatory BCS term takes the year's place.
    const yearVisible = examSelected && !isBcs && !isLanguageLevel(level);
    const yearRequired = yearVisible;

    const [availableTags, setAvailableTags] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    // Passage flow: how many questions have been saved-and-continued in this session, so the
    // header can show which question number is being added ("Question #3 for this passage").
    const [addedCount, setAddedCount] = useState(0);
    const [error, setError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const CallApi = useApiCaller();
    const voice = useVoiceDictation();

    // Shape the form state into the snake_case bag the shared AI hook + the
    // ingestion payload helpers expect. useMemo to give the hook a stable
    // reference per real change (every keystroke still produces a new object,
    // which is correct — the hook's stale-prediction effect watches primitives).
    const questionFields = useMemo(() => ({
        question_text: questionText,
        option_a: optionA,
        option_b: optionB,
        option_c: optionC,
        option_d: optionD,
        correct_option: correctOption,
        level,
    }), [questionText, optionA, optionB, optionC, optionD, correctOption, level]);

    const ai = useQuestionAi({
        questionFields,
    });

    // Auto-fetch similar questions once when a level is first set so the
    // panel surfaces existing duplicates while the admin types. Subsequent
    // refreshes go through the explicit footer button.
    const didInitialSimilarFetchRef = useRef(false);
    useEffect(() => {
        if (!level || didInitialSimilarFetchRef.current) return;
        didInitialSimilarFetchRef.current = true;
        ai.fetchSimilar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level]);

    // Keep the primary `level` synced with the first selected level (drives taxonomy pickers).
    useEffect(() => {
        setLevel(levels[0] || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [levels]);

    const fetchTagsForLevel = useCallback(async () => {
        // Fetch tags for EVERY selected level and merge, so the Exam picker offers exams across
        // all selected levels (subject/topic remain scoped to the primary level).
        const lvls = (levels || []).filter(l => l && l !== "All Levels");
        if (!lvls.length) { setAvailableTags([]); return; }
        try {
            const results = await Promise.all(
                lvls.map(l => CallApi({ url: getTagUrl({ level: l }), method: 'GET', token: true }))
            );
            const merged = [];
            results.forEach(r => { if (r && r.success) merged.push(...(r.data.tags || [])); });
            setAvailableTags(merged);
        } catch (err) {
            setAvailableTags([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [levels]);

    useEffect(() => {
        fetchTagsForLevel();
    }, [fetchTagsForLevel]);

    // Clear the level-dependent selections only when the level actually CHANGES from a prior
    // value — NOT on mount and NOT on StrictMode's dev double-invoke — so prefilled group
    // defaults (level/subject/division/unit/topic) survive. (A mount-ref guard is unreliable
    // under StrictMode because the ref persists across the double-mount.)
    const prevLevelRef = useRef(defaultLevel || '');
    useEffect(() => {
        if (prevLevelRef.current === level) return;
        prevLevelRef.current = level;
        setSelectedSubjects([]);
        setSelectedTopics([]);
        setSelectedExams([]);
        setSelectedBcsTerm('');
        setBoard('');
        setDivision('');
        setUnit('');
    }, [level]);

    // When an exam is selected, auto-select the coupled division (SSC/HSC) or unit (VARSITY)
    // with a default if applicable and none is chosen yet.
    useEffect(() => {
        if (!examSelected) return;
        if (isUnitLevel(level) && !unit) setUnit(UNIT_VALUES[0]);
        if (isDivisionLevel(level) && !division) setDivision(DIVISION_VALUES[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examSelected, level]);


    const resetForm = () => {
        setQuestionText('');
        setOptionA('');
        setOptionB('');
        setOptionC('');
        setOptionD('');
        setOptionE('');
        setOptionF('');
        setOptionG('');
        setOptionH('');
        setExtraOptionsShown(0);
        setBlanks([{ choices: ['', '', ''], correct: 0 }, { choices: ['', '', ''], correct: 0 }]);
        setCorrectOptions([]);
        setTextAnswer('');
        setAnswerExplanation('');
        setSelectedTopics([]);
        setSelectedExams([]);
        setSelectedYears([]);
        setSideText('');
        setAudioUrl('');
        setSolvingTimeSeconds('');
        setQuestionImageUrl('');
        setOptionImages({ A: '', B: '', C: '', D: '', E: '', F: '', G: '', H: '' });
        setExplanationImageUrl('');
        // Keep level / subjects / difficulty / BCS term / board / division / unit so an
        // admin can rapidly add a batch of questions for the same context. Clear the error.
        setError('');
    };

    const toggleCorrectOption = (opt) => {
        setCorrectOptions(prev =>
            prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]
        );
    };

    // A question text / option counts as "filled" if it has TEXT or an IMAGE.
    const hasQuestionContent = !!(questionText || questionImageUrl);
    const optionFilled = (key, text) => !!(text || optionImages[key]);
    const allOptionsFilled = optionFilled('A', optionA) && optionFilled('B', optionB)
        && optionFilled('C', optionC) && optionFilled('D', optionD);

    // Per-type answer validity used by handleSave + the save-button disabled state.
    const blanksValid = blanks.length >= 2 && blanks.length <= 3
        && blanks.every(b => b.choices.filter(c => (c || '').trim()).length === 3
            && b.correct >= 0 && b.correct <= 2);
    const seValid = presentLetters.length === 6 && correctOptions.length === 2;
    // Build-a-Sentence: ≥2 tiles (present options) and an ordered subset of distinct tile
    // indices (≥2). The order need NOT use every tile — leftover tiles are distractors.
    const buildValid = presentLetters.length >= 2 && correctOrder.length >= 2
        && new Set(correctOrder).size === correctOrder.length
        && correctOrder.every(i => Number.isInteger(i) && i >= 0 && i < presentLetters.length);
    // MATCHING: ≥2 filled box rows and at least one correct letter, all within the filled rows.
    const filledMatchRows = matchRows.filter(r => (r.text || '').trim());
    const matchValid = filledMatchRows.length >= 2 && correctOptions.length >= 1
        && correctOptions.every(l => filledMatchRows.some(r => r.letter === l));
    const answerValid = isText
        ? true
        : isTC
            ? blanksValid
            : isSE
                ? seValid
                : isBuild
                    ? buildValid
                    : isMatching
                        ? matchValid
                        : (isEssay || (allOptionsFilled && correctOptions.length > 0 && correctOptions.every(o => presentLetters.includes(o))));

    const handleSave = async (addAnother = false) => {
        setError('');
        const baseValid = hasQuestionContent && level && selectedSubjects.length > 0 && difficulty;
        const unitValid = !isUnitLevel(level) || !!unit;
        if (!baseValid || !answerValid) {
            if (isTC && !blanksValid) setError('Text Completion needs 2–3 blanks, each with 3 choices and a correct pick.');
            else if (isSE && !seValid) setError('Sentence Equivalence needs exactly 6 options and exactly 2 correct.');
            else if (isBuild && !buildValid) setError('Build a Sentence needs ≥2 word tiles and at least 2 placed in the correct order (leftover tiles are distractors).');
            else if (isMatching && !matchValid) setError('Matching needs at least 2 option-box entries and a correct letter chosen for this item.');
            else setError('Please fill all required fields (text or image is fine).');
            return;
        }
        if (!unitValid) {
            setError('Unit is required for VARSITY questions.');
            return;
        }
        if (divisionRequired && !division) {
            setError('Division is required for SSC/HSC questions.');
            return;
        }
        if (bcsTermRequired && !selectedBcsTerm) {
            setError('BCS Term is required for BCS questions.');
            return;
        }
        if (yearRequired && selectedYears.length === 0) {
            setError('Exam Year is required when an exam is selected.');
            return;
        }
        if (boardRequired && !board) {
            setError('Board is required when an exam is selected.');
            return;
        }
        setIsSaving(true);
        try {
            // Build option text + image maps keyed by field name, limited to present options
            // (blank extras are dropped). TEXT_COMPLETION sends no options; its blanks go in metadata.
            const options = {};
            const optionImagesByField = {};
            if (showOptionsGrid) {
                presentLetters.forEach(l => {
                    options[`option_${l.toLowerCase()}`] = optionsByLetter[l] || '';
                    if (optionImages[l]) optionImagesByField[`option_${l.toLowerCase()}`] = optionImages[l];
                });
            }
            const acceptedList = acceptedAnswers.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
            const matchBox = {};
            matchRows.forEach(r => { if ((r.text || '').trim()) matchBox[r.letter] = r.text.trim(); });
            const metadata = isTC
                ? { blanks: blanks.map(b => ({ choices: b.choices.map(c => (c || '').trim()), correct: b.correct })) }
                : isBuild
                    ? { correct_order: correctOrder }
                    : isMatching
                        ? { match_options: matchBox, ...(matchImage.trim() ? { match_image: matchImage.trim() } : {}) }
                        : (isText && acceptedList.length > 0)
                            ? { accepted: acceptedList }
                            : undefined;
            const payload = constructQuestionPostPayload({
                text: questionText,
                options,
                optionImages: optionImagesByField,
                correctOption: (isTC || isBuild) ? '' : correctOption,
                answerExplanation,
                level,
                levels,
                topic: selectedTopics.join(','),
                subject: selectedSubjects.join(','),
                exam: selectedExams.join(','),
                difficulty,
                year: isBcs ? '' : selectedYears.join(','),
                bcsTerm: isBcs ? selectedBcsTerm : '',
                syllabusStatus,
                questionType,
                textAnswer,
                board, division, unit,
                sideText, audioUrl, solvingTimeSeconds,
                questionImageUrl,
                explanationImageUrl,
                metadata,
                groupId,
                groupOrder,
            });
            const newQuestionPostUrl = getAdminQuestionPostUrl();
            const response = await CallApi({
                url: newQuestionPostUrl,
                method: 'POST',
                payload: payload,
                token: true
            });
            if (response.success) {
                if (onSaved) onSaved();
                if (addAnother) {
                    // Stay on the form, cleared, so the next question can be entered.
                    setAddedCount(c => c + 1);
                    resetForm();
                } else {
                    onSave();
                }
            } else {
                setError(response.message || 'Failed to add question.');
            }
        } catch (e) {
            setError('An error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOptionChange = (optionKey, value) => {
        const setter = optionSetters[optionKey];
        if (setter) setter(value);
    };

    const optionsMap = optionsByLetter;

    const inDisabled =
        !hasQuestionContent || !level || selectedSubjects.length === 0 || !difficulty ||
        !answerValid ||
        (isUnitLevel(level) && !unit) ||
        (divisionRequired && !division) ||
        (bcsTermRequired && !selectedBcsTerm) ||
        (yearRequired && selectedYears.length === 0) ||
        (boardRequired && !board);

    // Human-readable reason the Save buttons are disabled, so MATCHING/IELTS (and every other
    // type) shows exactly what's still required instead of a silently-greyed button.
    const disabledReason = (() => {
        if (!hasQuestionContent) return 'Add the question text (or an image).';
        if (!level) return 'Select a level.';
        if (selectedSubjects.length === 0) return 'Select at least one subject.';
        if (!difficulty) return 'Select a difficulty.';
        if (!answerValid) {
            if (isMatching) return 'Matching needs ≥2 option-box entries and a correct letter ticked for this item.';
            if (isTC) return 'Text Completion needs 2–3 blanks, each with 3 choices and a correct pick.';
            if (isSE) return 'Sentence Equivalence needs exactly 6 options and exactly 2 correct.';
            if (isBuild) return 'Build a Sentence needs ≥2 tiles and ≥2 placed in the correct order.';
            return 'Fill the options and tick the correct answer.';
        }
        if (isUnitLevel(level) && !unit) return 'Unit is required for VARSITY questions.';
        if (divisionRequired && !division) return 'Division is required for SSC/HSC questions.';
        if (bcsTermRequired && !selectedBcsTerm) return 'BCS Term is required for BCS questions.';
        if (yearRequired && selectedYears.length === 0) return 'Exam Year is required when an exam is selected.';
        if (boardRequired && !board) return 'Board is required when an exam is selected.';
        return '';
    })();

    const handleTagChange = (setter, currentTags, newValue) => {
        if (newValue && !currentTags.includes(newValue)) {
            setter([...currentTags, newValue]);
        }
    };

    const handleRemoveTag = (setter, currentTags, tagToRemove) => {
        setter(currentTags.filter(tag => tag !== tagToRemove));
    };

    const getUniqueTagsForType = (type) => {
        return [...new Set(availableTags.filter(tag => tag.tag_type === type && tag.level === level).map(tag => tag.name))];
    };

    // Exam options span EVERY selected level, deduped by name (subject/topic stay primary-scoped).
    const getExamOptions = () => {
        const lvls = new Set((levels || []).filter(Boolean));
        return [...new Set(
            availableTags.filter(tag => tag.tag_type === "EXAM" && lvls.has(tag.level)).map(tag => tag.name)
        )];
    };

    // Topics for the current level, narrowed to the selected subject(s) via the tag's subject
    // field (fallback: legacy metadata.subject). Topics with no subject are kept (degrades to
    // level-only where the link is absent).
    const getTopicOptions = () => {
        const subs = selectedSubjects || [];
        return [...new Set(
            availableTags
                .filter(t => t.tag_type === 'TOPIC' && t.level === level)
                .filter(t => {
                    const s = t.subject || (t.metadata && t.metadata.subject) || '';
                    return !s || subs.length === 0 || subs.includes(s);
                })
                .map(t => t.name)
        )];
    };

    // BCS term tag list (sorted newest-first). BCS sittings carry no exam year — the term is
    // the grouping key, so nothing is auto-filled into the year field.
    const bcsTermTags = level === BCS_LEVEL
        ? availableTags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL)
        : [];
    const bcsTermNames = sortBcsTerms(bcsTermTags.map(t => t.name));

    const handleBcsTermChange = (newTerm) => {
        setSelectedBcsTerm(newTerm);
    };

    const topSimilarity = ai.similar.length > 0 ? ai.similar[0].similarity_score : 0;
    const showDuplicateWarning = ai.similarFetched && topSimilarity >= SIMILARITY_THRESHOLDS.DUPLICATE;

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                role="document"
                style={{ maxWidth: '1500px', width: '95vw' }}
            >
                <div className="modal-content kp-qform">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}{groupId ? ` — Question #${passageStartCount + addedCount + 1} for this passage` : ''}</h5>
                        <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-sm" style={{ background: "linear-gradient(145deg, #8b5cf6, #6d28d9)", color: "#fff", border: "none", fontWeight: 600, whiteSpace: "nowrap", marginLeft: "1rem" }} onClick={() => setShowPreview(true)}>👁 Preview Question In Test</button>
                            <CloseButton onClick={onCancel} />
                        </div>
                    </div>
                    <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'hidden' }}>
                        {showDuplicateWarning && (
                            <div className="alert alert-warning py-2 mb-3" role="alert">
                                <strong>⚠ Near-duplicate detected:</strong>{' '}
                                this question is {topSimilarity.toFixed(2)} similar to question #{ai.similar[0].question_id}.
                                Review the right panel before saving.
                            </div>
                        )}
                        {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

                        <div className="row g-3" style={{ height: 'calc(100vh - 320px)' }}>
                            <div className="col-md-7" style={{ overflowY: 'auto', maxHeight: '100%', overscrollBehavior: 'contain' }}>
                                <div className="d-flex align-items-center mb-3 gap-2">
                                    <small className="text-muted">Voice input:</small>
                                    <div className="btn-group btn-group-sm" role="group" aria-label="Voice language">
                                        <button
                                            type="button"
                                            className={`btn ${voice.language === 'en' ? 'btn-info' : 'btn-outline-info'}`}
                                            onClick={() => voice.setLanguage('en')}
                                            disabled={!voice.supported}
                                        >English</button>
                                        <button
                                            type="button"
                                            className={`btn ${voice.language === 'bn' ? 'btn-info' : 'btn-outline-info'}`}
                                            onClick={() => voice.setLanguage('bn')}
                                            disabled={!voice.supported}
                                        >বাংলা</button>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Level(s) <span className="kp-req">*</span></label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {levels.map((lv, i) => (
                                            <span key={lv} className={`badge d-flex align-items-center rounded-pill px-2 py-1 ${i === 0 ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'}`}>
                                                {lv}{i === 0 ? ' (primary)' : ''}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setLevels, levels, lv)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setLevels, levels, e.target.value)}>
                                        <option value="">Add Level</option>
                                        {TAGLEVELS.map(item => item.submenu ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup> : <option key={item.value} value={item.value}>{item.label}</option>)}
                                    </select>
                                    <small className="form-text text-muted">Pick one or more levels. The first is the primary and drives the subject/topic pickers.</small>
                                </div>
                                {isDivisionLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Division <span className="kp-req">*</span></label>
                                        <select className="form-select" value={division} onChange={e => setDivision(e.target.value)} required>
                                            <option value="">Select division</option>
                                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required for SSC/HSC questions.</small>
                                    </div>
                                )}
                                {isUnitLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Unit <span className="kp-req">*</span></label>
                                        <select className="form-select" value={unit} onChange={e => setUnit(e.target.value)} required>
                                            <option value="">Select unit</option>
                                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required for VARSITY questions.</small>
                                    </div>
                                )}
                                {/* Subject Multi-select — placed right after Level for a natural top-down flow. */}
                                <div className="mb-3">
                                    <label className="form-label">Subject <span className="kp-req">*</span></label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedSubjects.map(tag => (
                                            <span key={tag} className="badge bg-info-subtle d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedSubjects, selectedSubjects, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedSubjects, selectedSubjects, e.target.value)} disabled={!level || getUniqueTagsForType("SUBJECT").length === 0}>
                                        <option value="">Add Subject</option>
                                        {getUniqueTagsForType("SUBJECT").map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {!level && <small className="form-text text-muted">Select a Level to add Subjects.</small>}
                                </div>

                                {/* Topic Multi-select */}
                                <div className="mb-3">
                                    <label className="form-label">Topic</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedTopics.map(tag => (
                                            <span key={tag} className="badge bg-warning-subtle d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedTopics, selectedTopics, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedTopics, selectedTopics, e.target.value)} disabled={!level || getTopicOptions().length === 0}>
                                        <option value="">Add Topic</option>
                                        {getTopicOptions().map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {!level && <small className="form-text text-muted">Select a Level to add Topics.</small>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label me-3">Question Type</label>
                                    {QUESTION_TYPES.map(qt => (
                                        <div className="form-check form-check-inline" key={qt}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="question_type_add"
                                                id={`qtype-${qt}`}
                                                checked={questionType === qt}
                                                onChange={() => setQuestionType(qt)}
                                            />
                                            <label className="form-check-label" htmlFor={`qtype-${qt}`}>
                                                {QUESTION_TYPE_LABELS[qt] || qt}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {isLanguageLevel(level) && (
                                    <>
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="form-label mb-0">Side text / passage (optional)</label>
                                                <VoiceInputButton
                                                    fieldKey="side_text"
                                                    listeningField={voice.listeningField}
                                                    supported={voice.supported}
                                                    onClick={() => voice.toggle('side_text', sideText, setSideText)}
                                                />
                                            </div>
                                            <textarea className="form-control" value={sideText} onChange={e => setSideText(e.target.value)} onBlur={() => setSideText(normalizeFieldText(sideText))}></textarea>
                                        </div>
                                        <MediaUploadField label="Audio (optional)" value={audioUrl} onChange={setAudioUrl} kind="audio" />
                                    </>
                                )}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label mb-0">Question Text <span className="kp-req">*</span></label>
                                        <VoiceInputButton
                                            fieldKey="question"
                                            listeningField={voice.listeningField}
                                            supported={voice.supported}
                                            onClick={() => voice.toggle('question', questionText, setQuestionText)}
                                        />
                                    </div>
                                    <MediaUploadField label="Question image (optional)" value={questionImageUrl} onChange={setQuestionImageUrl} kind="image" />
                                    <textarea className="form-control" value={questionText} onChange={e => setQuestionText(e.target.value)} onBlur={() => setQuestionText(normalizeFieldText(questionText, { dashWhenUnsure: true }))} required></textarea>
                                    {questionText && questionText.indexOf('$') !== -1 && (
                                        <div className="small text-muted mt-1">Preview: <MathText text={questionText} /></div>
                                    )}
                                </div>
                                {isText && (
                                    <div className="mb-3">
                                        <label className="form-label mb-0">Text Answer (reference)</label>
                                        <small className="form-text text-muted d-block">Set a reference to auto-grade (case/space-insensitive). Leave blank to grade manually. Answer-key notation is accepted: <code>10/ten</code>, <code>grass(es)</code>, <code>(land) surface</code>.</small>
                                        <textarea className="form-control" value={textAnswer} onChange={e => setTextAnswer(e.target.value)}></textarea>
                                        <label className="form-label mb-0 mt-2">Accepted alternatives (optional)</label>
                                        <small className="form-text text-muted d-block">One per line or comma-separated — any of these also counts as correct.</small>
                                        <textarea className="form-control" rows={2} value={acceptedAnswers} onChange={e => setAcceptedAnswers(e.target.value)} placeholder="e.g. colour&#10;color"></textarea>
                                    </div>
                                )}
                                {isMatching && (
                                    <div className="mb-3 border rounded p-2">
                                        <label className="form-label mb-1 fw-bold">Matching option box (shared)</label>
                                        <small className="form-text text-muted d-block mb-2">The list of options A, B, C… that every item in this set chooses from. Tick the one(s) that are correct for THIS item.</small>
                                        {matchRows.map((row, i) => (
                                            <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                                <span className="fw-bold" style={{ width: '1.5rem' }}>{row.letter}</span>
                                                <input className="form-control form-control-sm" value={row.text}
                                                    onChange={e => setMatchRows(rows => rows.map((r, j) => j === i ? { ...r, text: e.target.value } : r))}
                                                    placeholder={`Option ${row.letter} text`} />
                                                <div className="form-check mb-0" title="Correct for this item">
                                                    <input type="checkbox" className="form-check-input" checked={correctOptions.includes(row.letter)}
                                                        onChange={() => setCorrectOptions(cur => cur.includes(row.letter) ? cur.filter(l => l !== row.letter) : [...cur, row.letter])} />
                                                    <label className="form-check-label small">correct</label>
                                                </div>
                                                {matchRows.length > 2 && (
                                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setMatchRows(rows => rows.filter((_, j) => j !== i))}>×</button>
                                                )}
                                            </div>
                                        ))}
                                        {matchRows.length < 10 && (
                                            <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
                                                onClick={() => setMatchRows(rows => [...rows, { letter: String.fromCharCode(65 + rows.length), text: '' }])}>
                                                + Add option
                                            </button>
                                        )}
                                        <div className="mt-2">
                                            <MediaUploadField label="Shared map / plan / diagram image (optional)" value={matchImage} onChange={setMatchImage} kind="image" />
                                        </div>
                                    </div>
                                )}
                                {showOptionsGrid && visibleLetters.map((opt) => (
                                    <div className="mb-3" key={opt}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label mb-0">Option {opt}{optionsRequired && opt <= 'D' ? <span className="kp-req"> *</span> : ''}</label>
                                            {opt <= 'D' && (
                                                <VoiceInputButton
                                                    fieldKey={`option${opt}`}
                                                    listeningField={voice.listeningField}
                                                    supported={voice.supported}
                                                    onClick={() => voice.toggle(`option${opt}`, optionsMap[opt], (text) => handleOptionChange(opt, text))}
                                                />
                                            )}
                                        </div>
                                        <MediaUploadField label={`Option ${opt} image (optional)`} value={optionImages[opt]} onChange={(url) => setOptionImages(prev => ({ ...prev, [opt]: url }))} kind="image" />
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={optionsMap[opt]}
                                            onChange={e => handleOptionChange(opt, e.target.value)}
                                            onBlur={() => handleOptionChange(opt, normalizeFieldText(optionsMap[opt]))}
                                            required={optionsRequired && opt <= 'D'}
                                        />
                                    </div>
                                ))}

                                {/* Add-more-options controls (MCQ only; SE is fixed at 6, blank = absent). */}
                                {showOptionsGrid && !isSE && extraOptionsShown < 4 && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary mb-3"
                                        onClick={() => setExtraOptionsShown(n => Math.min(4, n + 2))}>
                                        + Add two more options
                                    </button>
                                )}
                                {isSE && (
                                    <div className="form-text text-muted mb-2">Sentence Equivalence: fill all 6 options and tick exactly the 2 correct answers.</div>
                                )}

                                {showOptionsGrid && !isBuild && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Correct Option(s){optionsRequired ? <span className="kp-req"> *</span> : ''}</label>
                                        <div className="d-flex flex-wrap gap-3">
                                            {presentLetters.map(opt => (
                                                <div className="form-check" key={opt}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`correct-${opt}`}
                                                        checked={correctOptions.includes(opt)}
                                                        onChange={() => toggleCorrectOption(opt)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`correct-${opt}`}>{opt}</label>
                                                </div>
                                            ))}
                                        </div>
                                        <small className="form-text text-muted">
                                            {isSE ? 'Tick exactly two correct options.' : 'Tick every correct option — supports “select all that apply”. Only filled options are shown.'}
                                        </small>
                                    </div>
                                )}

                                {/* TOEFL Build a Sentence: tiles are the option fields; click them in order. */}
                                {isBuild && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Correct word order <span className="kp-req">*</span></label>
                                        <div className="form-text text-muted mb-2">Fill the option fields above with the (shuffled) word tiles, then click them in the correct sentence order. Leftover tiles act as distractors — you don't have to place every tile.</div>
                                        <div className="border rounded p-2 mb-2 d-flex flex-wrap gap-2" style={{ minHeight: '2.2rem' }}>
                                            {correctOrder.length === 0 && <span className="text-muted">Click tiles below to set the order…</span>}
                                            {correctOrder.filter(i => i < presentLetters.length).map((i, pos) => (
                                                <button type="button" key={pos} className="btn btn-sm btn-primary" onClick={() => setCorrectOrder(o => o.filter(x => x !== i))} title="Click to remove">
                                                    {pos + 1}. {optionsByLetter[presentLetters[i]]}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {presentLetters.map((opt, i) => (
                                                <button type="button" key={opt} disabled={correctOrder.includes(i)} className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setCorrectOrder(o => [...o, i])}>
                                                    {optionsByLetter[opt] || opt}
                                                </button>
                                            ))}
                                        </div>
                                        {correctOrder.length > 0 && (
                                            <button type="button" className="btn btn-sm btn-link text-danger p-0 mt-1" onClick={() => setCorrectOrder([])}>Reset order</button>
                                        )}
                                    </div>
                                )}

                                {/* GRE Text Completion: 2–3 blanks, each 3 choices, pick the correct one. */}
                                {isTC && (
                                    <div className="mb-3">
                                        <label className="form-label mb-1 d-block">Blanks <span className="kp-req">*</span></label>
                                        <div className="form-text text-muted mb-2">Use markers like (i), (ii) in the question text. Each blank has 3 choices; select the correct one. All-or-nothing scoring.</div>
                                        {blanks.map((blank, bi) => (
                                            <div className="border rounded p-2 mb-2" key={bi}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="fw-semibold">Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</span>
                                                    {blanks.length > 1 && (
                                                        <button type="button" className="btn btn-sm btn-outline-danger"
                                                            title={`Remove Blank ${TC_BLANK_LABELS[bi] || `#${bi + 1}`}`}
                                                            onClick={() => setBlanks(prev => prev.filter((_, i) => i !== bi))}>×</button>
                                                    )}
                                                </div>
                                                {blank.choices.map((choice, ci) => (
                                                    <div className="d-flex align-items-center gap-2 mb-1" key={ci}>
                                                        <input
                                                            type="radio"
                                                            name={`tc-blank-${bi}`}
                                                            checked={blank.correct === ci}
                                                            onChange={() => setBlanks(prev => prev.map((b, i) => i === bi ? { ...b, correct: ci } : b))}
                                                            title="Mark correct"
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder={`Choice ${ci + 1}`}
                                                            value={choice}
                                                            onChange={e => setBlanks(prev => prev.map((b, i) => i === bi ? { ...b, choices: b.choices.map((c, j) => j === ci ? e.target.value : c) } : b))}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                        <div className="d-flex gap-2">
                                            {blanks.length < 3 && (
                                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setBlanks(prev => [...prev, { choices: ['', '', ''], correct: 0 }])}>+ Add blank</button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                                        <label className="form-label mb-0">Explanation</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                title="Collapse line breaks into one clean paragraph"
                                                onClick={() => setAnswerExplanation(beautifyText(answerExplanation))}
                                            >Beautify</button>
                                            <VoiceInputButton
                                                fieldKey="explanation"
                                                listeningField={voice.listeningField}
                                                supported={voice.supported}
                                                onClick={() => voice.toggle('explanation', answerExplanation, setAnswerExplanation)}
                                            />
                                        </div>
                                    </div>
                                    <MediaUploadField label="Explanation image (optional)" value={explanationImageUrl} onChange={setExplanationImageUrl} kind="image" />
                                    <textarea className="form-control" value={answerExplanation} onChange={e => setAnswerExplanation(e.target.value)} onBlur={() => setAnswerExplanation(normalizeFieldText(answerExplanation))}></textarea>
                                    {answerExplanation && answerExplanation.indexOf('$') !== -1 && (
                                        <div className="small text-muted mt-1">Preview: <MathText text={answerExplanation} /></div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Difficulty <span className="kp-req">*</span></label>
                                    <select className="form-select" value={difficulty} onChange={e => setDifficulty(e.target.value)} required>
                                        <option value="">Select</option>
                                        {QUESTIONDIFFICULTY.map(diff => <option key={diff} value={diff}>{diff}</option>)}
                                    </select>
                                </div>

                                {/* Exam Multi-select — hidden for IELTS/TOEFL/GRE */}
                                {!isLanguageLevel(level) && (
                                <div className="mb-3">
                                    <label className="form-label">Exam</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedExams.map(tag => (
                                            <span key={tag} className="badge bg-danger-subtle d-flex align-items-center rounded-pill px-2 py-1">
                                                {tag}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedExams, selectedExams, tag)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    <select className="form-select" value="" onChange={e => handleTagChange(setSelectedExams, selectedExams, e.target.value)} disabled={levels.length === 0 || getExamOptions().length === 0}>
                                        <option value="">Add Exam</option>
                                        {getExamOptions().map(tag => <option key={tag} value={tag}>{tag}</option>)}
                                    </select>
                                    {levels.length === 0 && <small className="form-text text-muted">Select a Level to add Exams.</small>}
                                </div>
                                )}

                                {isBoardLevel(level) && examSelected && (
                                    <div className="mb-3">
                                        <label className="form-label">Board <span className="kp-req">*</span></label>
                                        <select className="form-select" value={board} onChange={e => setBoard(e.target.value)} required>
                                            <option value="">Select board</option>
                                            {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                        <small className="form-text text-muted">Required when an exam is selected.</small>
                                    </div>
                                )}
                                {isTimerLevel(level) && (
                                    <div className="mb-3">
                                        <label className="form-label">Solving time (seconds, optional)</label>
                                        <input type="number" min="0" className="form-control" value={solvingTimeSeconds} onChange={e => setSolvingTimeSeconds(e.target.value)} />
                                    </div>
                                )}

                                {isBcs && (
                                    <div className="mb-3">
                                        <label className="form-label">BCS Term <span className="kp-req">*</span></label>
                                        <select
                                            className="form-select"
                                            value={selectedBcsTerm}
                                            onChange={e => handleBcsTermChange(e.target.value)}
                                            disabled={bcsTermNames.length === 0}
                                            required
                                        >
                                            <option value="">Select BCS term</option>
                                            {bcsTermNames.map(name => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                        <small className="form-text text-muted">
                                            Required for BCS exam questions. The question is auto-added to this term's Previous-Year test. Add new terms in the Tag page.
                                        </small>
                                    </div>
                                )}

                                {yearVisible && (
                                <div className="mb-3">
                                    <label className="form-label">Exam Year {yearRequired ? <span className="kp-req">*</span> : <span className="text-muted">(optional)</span>}</label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {selectedYears.map(y => (
                                            <span key={y} className="badge bg-secondary-subtle text-secondary d-flex align-items-center rounded-pill px-2 py-1">
                                                {y}
                                                <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => handleRemoveTag(setSelectedYears, selectedYears, y)}></button>
                                            </span>
                                        ))}
                                    </div>
                                    {(() => {
                                        const currentYear = new Date().getFullYear();
                                        return (
                                            <YearPicker
                                                value=""
                                                onChange={y => handleTagChange(setSelectedYears, selectedYears, y)}
                                                minYear={currentYear - 20}
                                                maxYear={currentYear}
                                                placeholder="Add Exam Year"
                                                isClearable={false}
                                            />
                                        );
                                    })()}
                                    <small className="form-text text-muted">{yearRequired ? 'Required when an exam is selected — the exam year(s) the question appeared in.' : 'Optional — the exam year(s) the question appeared in.'}</small>
                                </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Syllabus status</label>
                                    <select className="form-select" value={syllabusStatus} onChange={e => setSyllabusStatus(e.target.value)}>
                                        {SYLLABUS_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    <small className="form-text text-muted">Current = in the syllabus (served everywhere). Outdated = kept for the Previous-Year bank only. Withdrawn = hidden everywhere.</small>
                                </div>

                                {/* Similar Existing Question — single top match, at the bottom of the details column. */}
                                <div className="mt-3 pt-3 border-top">
                                    <h6 className="mb-2">Similar Existing Question</h6>
                                    <SimilarQuestionsPanel
                                        loading={ai.similarLoading}
                                        items={ai.similar}
                                        hasFetched={ai.similarFetched}
                                    />
                                </div>
                            </div>

                            <div
                                className="col-md-5 border-start ps-3 d-flex flex-column"
                                style={{ maxHeight: '100%', overflow: 'hidden' }}
                            >
                                {setSourceDoc && (
                                    <SourceDocViewer
                                        doc={sourceDoc}
                                        onDoc={setSourceDoc}
                                        onClose={() => setSourceDoc(null)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-info me-auto"
                            onClick={ai.fetchSimilar}
                            disabled={ai.similarLoading || isSaving}
                        >
                            {ai.similarLoading ? 'Loading…' : 'Re-fetch Relevant Questions'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>{cancelLabel}</button>
                        {onBack && (
                            <button type="button" className="btn btn-outline-secondary" onClick={onBack} disabled={isSaving}>{backLabel}</button>
                        )}
                        <button type="button" className="btn btn-outline-primary" onClick={() => handleSave(true)} disabled={inDisabled || isSaving} title={inDisabled ? disabledReason : ''}>
                            {isSaving ? 'Saving…' : addAnotherLabel}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={() => handleSave(false)} disabled={inDisabled || isSaving} title={inDisabled ? disabledReason : ''}>
                            {isSaving ? 'Saving…' : saveLabel}
                        </button>
                        {inDisabled && !isSaving && disabledReason && (
                            <div className="w-100 text-end small text-warning mt-1">⚠ {disabledReason}</div>
                        )}
                    </div>
                </div>
            </div>
            <QuestionPreviewModal
                open={showPreview}
                onClose={() => setShowPreview(false)}
                fields={{
                    questionText, questionType, correctOptions,
                    options: optionsByLetter, optionImages,
                    questionImageUrl, blanks, textAnswer, sideText, audioUrl,
                    correctTextAnswer: textAnswer, answerExplanation, explanationImageUrl,
                }}
                sourceDoc={sourceDoc}
                setSourceDoc={setSourceDoc}
            />
        </div>
    );
};

export default QuestionAddForm;
